import { useState, useRef, useEffect } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import * as Dialog from "@radix-ui/react-dialog";

import { api, ApiError, type UserStructuredProps } from "../lib/api";
import { QUICK_ADD_SPECS } from "../lib/quickAddProps";
import { AddSourceDialog } from "../components/AddSourceDialog";
import { PlatformLogo } from "../components/PlatformLogo";
import { cn } from "../lib/cn";

// One device entry as returned verbatim by a Litmus Edge gateway's native
// asset-discovery API (GET /networkutil/discover/all/scan), plus the
// discovery_source the backend stamps on (and persists) after a scan.
interface LitmusEdgeDevice {
  ip_address: string;
  mac_address: string;
  vendor: string;
  product_name: string;
  product_type: string;
  interface: string;
  last_seen: number; // unix seconds
  protocols: string[];
  protocols_data: Record<string, unknown>;
  discovery_source?: string; // Litmus Edge gateway host that reported this device
  // Best-guess registered connector for this device, resolved server-side
  // from vendor/product_name/protocols_data (see MatchLEConnector on the
  // backend) — empty when nothing matched confidently, in which case the
  // UI offers "Coming soon" instead of "Add Source".
  connectorId?: string;
  connectorName?: string;
  // The matched connector's own config field key for its PLC/controller
  // hostname (see attachConnectorMatches on the backend) - present only
  // when that connector's schema actually has one, so "Add Source" knows
  // it's safe to pre-fill this device's IP into it.
  connectorHostField?: string;
}

// One row of GET /api/v1/scan/litmusedge/sources - a discovery source
// (Litmus Edge gateway) summary driving a collapsible group header.
interface LitmusEdgeSource {
  host: string;
  deviceCount: number;
  lastScannedAt: string; // RFC3339
}

// Format a unix-seconds timestamp (as returned by the Litmus Edge discovery
// API) as a locale-formatted date/time string.
function formatEpochSeconds(seconds: number): string {
  if (!seconds) return "—";
  return new Date(seconds * 1000).toLocaleString();
}

export function AssetDiscoveryPage() {
  return (
    <div className="mx-auto max-w-5xl px-6 py-8">
      <div className="mb-6">
        <h1 className="font-display text-[28px] font-semibold tracking-tightest text-ink">
          Asset Discovery
        </h1>
        <p className="mt-1 text-[13.5px] text-ash">
          Discover assets on your plant network through a Litmus Edge gateway, then add them as
          sources.
        </p>
        <p className="mt-2 text-[12.5px] text-ash/80 leading-relaxed">
          Litmus Edge runs asset discovery on the gateway and reports every asset it finds,
          including vendor, model and protocols. No scan traffic is generated by this agent.
        </p>
      </div>

      <LitmusEdgeDiscoveryPanel />
    </div>
  );
}

// Connection lifecycle for the Litmus Edge discovery panel. "unreachable" and
// "error" are distinct so the UI can tell "never got past connectivity" apart
// from "connected fine but discovery itself failed" (e.g. a bad token).
type LitmusEdgeConnectionPhase =
  | "idle"
  | "testing"
  | "unreachable"
  | "scanning"
  | "connected"
  | "error";

const LITMUS_EDGE_STATUS_LABEL: Record<LitmusEdgeConnectionPhase, string> = {
  idle: "NOT CONNECTED",
  testing: "TESTING…",
  unreachable: "UNREACHABLE",
  scanning: "DISCOVERING…",
  connected: "CONNECTED",
  error: "ERROR",
};

const LITMUS_EDGE_STATUS_CLASS: Record<LitmusEdgeConnectionPhase, string> = {
  idle: "bg-rule/40 text-ash",
  testing: "bg-amber-100 text-amber-700",
  unreachable: "bg-rose-100 text-rose-700",
  scanning: "bg-amber-100 text-amber-700",
  connected: "bg-emerald-100 text-emerald-700",
  error: "bg-rose-100 text-rose-700",
};

// Extract a user-friendly message from a failed API call. The backend always
// returns {"error": "..."} on non-2xx responses (see ApiError in lib/api.ts),
// so this only needs a fallback for genuinely unexpected failures (e.g. the
// browser itself couldn't reach the local agent).
// describeError turns a failed scan/test call into an operator-facing
// message. The agent's own JSON {error} is already written for operators
// (see scanner.LEUserMessage on the backend), so it's shown as-is. Anything
// else - no JSON body (a proxy in front of the agent, the agent restarting)
// or the browser failing to reach the agent at all - would otherwise surface
// as "POST /api/... failed" or "Failed to fetch", so it's replaced here.
function describeError(err: unknown): string {
  if (err instanceof ApiError) {
    const body = err.body;
    const hasAgentMessage =
      !!body && typeof body === "object" && typeof (body as { error?: unknown }).error === "string";
    if (hasAgentMessage) return err.message;
    if (err.status === 504 || err.status === 408) {
      return "The request timed out before the gateway finished. Large networks can take a while to scan - wait a few minutes and try again.";
    }
    return "The Catalog Agent could not complete the request. Wait a moment and try again.";
  }
  if (err instanceof TypeError) {
    // fetch() rejects with a TypeError only when the request never got a
    // response (agent stopped, network dropped).
    return "Could not reach the Catalog Agent. Check it is running and your network connection, then try again.";
  }
  return "Something went wrong. Please try again.";
}

// Asset Discovery - connects to a Litmus Edge gateway's own asset-discovery
// API and lists everything it has already found on the network. The hostname/token used to connect live only in component state
// (never persisted) — but the scan *results* are persisted server-side
// (upserted per gateway + device, see /api/v1/scan/litmusedge/devices), so
// the table survives switching pages and agent restarts instead of
// resetting to empty every time this component remounts.
const LITMUS_EDGE_PAGE_SIZE = 10;

function LitmusEdgeDiscoveryPanel() {
  const [host, setHost] = useState("");
  const [token, setToken] = useState("");
  const [phase, setPhase] = useState<LitmusEdgeConnectionPhase>("idle");
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  // Which discovery-source groups are expanded, keyed by gateway host.
  const [expandedSources, setExpandedSources] = useState<Set<string>>(new Set());
  const [addSourceOpen, setAddSourceOpen] = useState(false);
  const [addSourcePrefill, setAddSourcePrefill] = useState<{
    connectorId: string;
    fields: Record<string, string>;
    suggestedName?: string;
    structuredProperties?: UserStructuredProps;
  } | null>(null);
  // The device currently shown in the "Device Information" dialog, or null
  // when it's closed.
  const [detailDevice, setDetailDevice] = useState<LitmusEdgeDevice | null>(null);

  const queryClient = useQueryClient();

  // One row per Litmus Edge gateway that has been scanned - the group
  // headers. Each group's own devices are fetched separately (and only
  // once expanded, see LitmusEdgeSourceGroup) rather than loading every
  // gateway's full device list up front.
  const sourcesQuery = useQuery({
    queryKey: ["litmusedge", "sources"],
    queryFn: () => api.get<{ sources: LitmusEdgeSource[] }>("/api/v1/scan/litmusedge/sources"),
    staleTime: Infinity,
  });
  const sources = sourcesQuery.data?.sources ?? [];

  function toggleExpanded(host: string) {
    setExpandedSources((prev) => {
      const next = new Set(prev);
      if (next.has(host)) {
        next.delete(host);
      } else {
        next.add(host);
      }
      return next;
    });
  }

  const isBusy = phase === "testing" || phase === "scanning";
  const canSubmit = host.trim() !== "" && token.trim() !== "" && !isBusy;

  // Only ever invoked for a device the backend already matched to a
  // registered connector (see connectorId on LitmusEdgeDevice) - the
  // per-device menu disables "Add Source" entirely otherwise, showing
  // "Coming soon" instead. Skips straight to that connector's configure
  // step rather than the picker. A vendor/product string is enough to name
  // the right connector, not enough to safely guess its file path or
  // credentials - the one field this can prefill with confidence is the
  // connector's own hostname field (connectorHostField, set server-side
  // only when that connector's schema actually has one) with this device's
  // IP, since that's exactly what was just discovered at that address. The
  // device identity the scan reported (vendor, product, serial, ...) is
  // carried into the Static Metadata section - see staticMetadataFromDevice.
  function handleAddSource(device: LitmusEdgeDevice) {
    if (!device.connectorId) return;
    const label = [device.vendor, device.product_name].filter(Boolean).join(" ");
    setAddSourcePrefill({
      connectorId: device.connectorId,
      fields: device.connectorHostField ? { [device.connectorHostField]: device.ip_address } : {},
      suggestedName: label ? `${label} (${device.ip_address})` : device.ip_address,
      structuredProperties: staticMetadataFromDevice(device),
    });
    setAddSourceOpen(true);
  }

  async function handleConnectAndFetch() {
    const trimmedHost = host.trim();
    const trimmedToken = token.trim();
    setErrorMessage(null);
    setPhase("testing");

    try {
      await api.post<{ reachable: boolean }>("/api/v1/scan/litmusedge/test", {
        host: trimmedHost,
      });
    } catch (err) {
      setPhase("unreachable");
      setErrorMessage(describeError(err));
      return;
    }

    setPhase("scanning");
    try {
      await api.post<{ devices: LitmusEdgeDevice[]; count: number }>(
        "/api/v1/scan/litmusedge/discover",
        { host: trimmedHost, token: trimmedToken },
      );
      // Refetch the group list and every group's device pages rather than
      // rendering the raw response directly — the persisted store reflects
      // the de-duplicated, pruned set for this gateway, which is what
      // re-running is meant to refresh. Auto-expand the gateway just
      // scanned so its results are immediately visible without an extra click.
      setExpandedSources((prev) => new Set(prev).add(trimmedHost));
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ["litmusedge", "sources"] }),
        queryClient.invalidateQueries({ queryKey: ["litmusedge", "devices"] }),
      ]);
      setPhase("connected");
    } catch (err) {
      setPhase("error");
      setErrorMessage(describeError(err));
    }
  }

  const buttonLabel =
    phase === "testing"
      ? "Testing…"
      : phase === "scanning"
        ? "Discovering…"
        : sources.length > 0
          ? "Discover again"
          : "Discover assets";

  return (
    <>
      <div className="card mb-6 px-6 py-5">
        <div className="mb-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <span className="flex h-6 w-6 items-center justify-center rounded bg-accent/10 text-[10px] font-semibold text-accent">
              LE
            </span>
            <h2 className="text-[14.5px] font-semibold text-ink">
              Litmus Edge gateway
            </h2>
          </div>
          <span
            className={`rounded-full px-2.5 py-1 text-[11px] font-medium tracking-wide ${LITMUS_EDGE_STATUS_CLASS[phase]}`}
          >
            {LITMUS_EDGE_STATUS_LABEL[phase]}
          </span>
        </div>

        <div className="flex flex-wrap items-end gap-4">
          <div className="flex-1 min-w-[200px]">
            <label className="mb-1 block text-[12px] font-medium text-ash">
              Gateway hostname
            </label>
            <input
              className="input w-full font-mono text-[13px]"
              placeholder="10.17.9.37"
              value={host}
              onChange={(e) => setHost(e.target.value)}
              disabled={isBusy}
            />
          </div>

          <div className="flex-1 min-w-[200px]">
            <label className="mb-1 block text-[12px] font-medium text-ash">
              API token
            </label>
            <input
              className="input w-full font-mono text-[13px]"
              type="password"
              value={token}
              onChange={(e) => setToken(e.target.value)}
              disabled={isBusy}
              autoComplete="off"
            />
          </div>

          <button
            className="btn-primary"
            onClick={handleConnectAndFetch}
            disabled={!canSubmit}
          >
            {buttonLabel}
          </button>
        </div>

        <p className="mt-3 text-[12px] text-ash/80 leading-relaxed">
          Runs Litmus Edge&apos;s native asset discovery on the gateway and lists every
          asset it finds on the network, well beyond a simple ARP sweep. Results stay
          listed below while you navigate this app, but are cleared once the agent
          restarts; discovering again on a gateway refreshes its assets and removes any it
          no longer reports. The token is from Litmus Edge{" "}
          <strong>System &gt; Access Control &gt; Tokens</strong>, used as the Basic-auth
          username; it is not saved and must be re-entered each visit.
        </p>
      </div>

      {errorMessage && (
        <div className="mb-4 rounded-md border border-rose-200 bg-rose-50 px-4 py-3 text-[13px] text-rose-700">
          {errorMessage}
        </div>
      )}

      {sources.length === 0 && phase !== "connected" && (
        <div className="rounded-md border border-dashed border-rule px-6 py-10 text-center">
          <div className="mx-auto mb-3 flex h-9 w-9 items-center justify-center rounded-full bg-emerald-50 text-emerald-600">
            ⭱
          </div>
          <div className="text-[13.5px] font-medium text-ink">No gateway connected</div>
          <p className="mt-1 text-[12.5px] text-ash">
            Enter the hostname and API token of a Litmus Edge gateway, then choose
            Discover assets to list everything it finds on that network.
          </p>
        </div>
      )}

      {sources.length === 0 && phase === "connected" && (
        <div className="rounded-md border border-rule px-6 py-10 text-center text-[13.5px] text-ash">
          Litmus Edge did not find any assets on this network.
        </div>
      )}

      {sources.length > 0 && (
        <div>
          {sources.map((s) => (
            <LitmusEdgeSourceGroup
              key={s.host}
              source={s}
              expanded={expandedSources.has(s.host)}
              onToggleExpand={() => toggleExpanded(s.host)}
              onAddSource={handleAddSource}
              onViewDetails={setDetailDevice}
            />
          ))}
        </div>
      )}

      <AddSourceDialog
        open={addSourceOpen}
        onOpenChange={setAddSourceOpen}
        prefill={addSourcePrefill ?? undefined}
      />
      <LitmusEdgeDeviceDetailDialog
        device={detailDevice}
        onOpenChange={(open) => {
          if (!open) setDetailDevice(null);
        }}
      />
    </>
  );
}

interface LitmusEdgeSourceGroupProps {
  source: LitmusEdgeSource;
  expanded: boolean;
  onToggleExpand: () => void;
  onAddSource: (device: LitmusEdgeDevice) => void;
  onViewDetails: (device: LitmusEdgeDevice) => void;
}

// One collapsible discovery-source group: a header (gateway host, device
// count, when it was last scanned) that expands into that gateway's own
// paginated device table. Each group fetches its own devices independently
// and only once expanded, so opening one large gateway's group doesn't
// require ever loading every other gateway's devices too.
function LitmusEdgeSourceGroup({
  source,
  expanded,
  onToggleExpand,
  onAddSource,
  onViewDetails,
}: LitmusEdgeSourceGroupProps) {
  const [page, setPage] = useState(0);

  const devicesQuery = useQuery({
    queryKey: ["litmusedge", "devices", source.host, page],
    queryFn: () =>
      api.get<{ devices: LitmusEdgeDevice[]; total: number }>(
        `/api/v1/scan/litmusedge/devices?source_host=${encodeURIComponent(source.host)}` +
          `&offset=${page * LITMUS_EDGE_PAGE_SIZE}&limit=${LITMUS_EDGE_PAGE_SIZE}`,
      ),
    staleTime: Infinity,
    enabled: expanded,
  });
  const devices = devicesQuery.data?.devices ?? [];
  // Falls back to the group-summary count before the first fetch resolves,
  // so the pager/range text don't flash "0 of 0" the instant a group opens.
  const total = devicesQuery.data?.total ?? source.deviceCount;
  const pageCount = Math.max(1, Math.ceil(total / LITMUS_EDGE_PAGE_SIZE));

  // Same "don't evict from a page that hasn't loaded yet" guard as the flat
  // list used before grouping - only a real, loaded page count can snap the
  // user back to a lower page.
  useEffect(() => {
    if (devicesQuery.isSuccess && page > 0 && page >= pageCount) {
      setPage(pageCount - 1);
    }
  }, [devicesQuery.isSuccess, page, pageCount]);

  return (
    <div className="card mb-3 overflow-hidden">
      <button
        type="button"
        onClick={onToggleExpand}
        aria-expanded={expanded}
        className="flex w-full items-center justify-between gap-4 px-4 py-3 text-left transition-colors hover:bg-paper-subtle"
      >
        <div className="flex items-center gap-3">
          <svg
            width="10"
            height="10"
            viewBox="0 0 10 10"
            fill="none"
            className={`shrink-0 text-ash transition-transform ${expanded ? "rotate-90" : ""}`}
            aria-hidden
          >
            <path
              d="M3 1l4 4-4 4"
              stroke="currentColor"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
          <span className="font-mono text-[13px] font-medium text-ink">{source.host}</span>
          <span className="rounded bg-rule/40 px-1.5 py-0.5 text-[11px] font-medium text-ash">
            {source.deviceCount} asset{source.deviceCount !== 1 ? "s" : ""}
          </span>
        </div>
        <span className="text-[11.5px] text-ash">
          Discovered {new Date(source.lastScannedAt).toLocaleString()}
        </span>
      </button>

      {expanded && (
        <div className="border-t border-rule">
          {devicesQuery.isLoading ? (
            <div className="px-4 py-6 text-center text-[12.5px] text-ash">Loading…</div>
          ) : (
            <>
              <div className="overflow-x-auto">
                <table className="w-full text-[13px]">
                  <thead>
                    <tr className="border-b border-rule bg-paper-subtle text-[11.5px] font-medium uppercase tracking-wide text-ash">
                      <th className="whitespace-nowrap px-4 py-3 text-left">IP Address</th>
                      <th className="whitespace-nowrap px-4 py-3 text-left">Vendor</th>
                      <th className="whitespace-nowrap px-4 py-3 text-left">Product Name</th>
                      <th className="whitespace-nowrap px-4 py-3 text-left">Product Type</th>
                      <th className="whitespace-nowrap px-4 py-3 text-right"></th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-rule">
                    {devices.map((d) => (
                      // IP is a true unique identity within one gateway now -
                      // the store keys and prunes strictly on it.
                      <LitmusEdgeDeviceRow
                        key={d.ip_address}
                        device={d}
                        onAddSource={onAddSource}
                        onViewDetails={onViewDetails}
                      />
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="flex items-center justify-between border-t border-rule px-4 py-2.5 text-[12px] text-ash">
                <span>
                  {Math.min(page * LITMUS_EDGE_PAGE_SIZE + 1, total)}
                  {"–"}
                  {Math.min((page + 1) * LITMUS_EDGE_PAGE_SIZE, total)} of {total}
                </span>
                <LitmusEdgePager page={page} pageCount={pageCount} total={total} onChange={setPage} />
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}

interface LitmusEdgePagerProps {
  page: number; // 0-based
  pageCount: number;
  total: number;
  onChange: (page: number) => void;
}

// Simple Prev/Next + "Page X of Y" pager. A device inventory tops out around
// a few hundred rows even across several gateways, so a numbered/ellipsis
// pager (as used for the much larger schema-field preview table) would be
// more UI than this data warrants.
function LitmusEdgePager({ page, pageCount, total, onChange }: LitmusEdgePagerProps) {
  if (total <= LITMUS_EDGE_PAGE_SIZE) return null;
  return (
    <div className="flex items-center gap-2">
      <button
        type="button"
        className="rounded border border-rule px-2 py-1 text-[11.5px] text-ash transition-colors hover:border-ash hover:text-ink disabled:cursor-not-allowed disabled:opacity-30"
        onClick={() => onChange(page - 1)}
        disabled={page <= 0}
      >
        ← Prev
      </button>
      <span className="text-[11.5px] text-ash">
        Page {page + 1} of {pageCount}
      </span>
      <button
        type="button"
        className="rounded border border-rule px-2 py-1 text-[11.5px] text-ash transition-colors hover:border-ash hover:text-ink disabled:cursor-not-allowed disabled:opacity-30"
        onClick={() => onChange(page + 1)}
        disabled={page >= pageCount - 1}
      >
        Next →
      </button>
    </div>
  );
}

interface LitmusEdgeDeviceRowProps {
  device: LitmusEdgeDevice;
  onAddSource: (device: LitmusEdgeDevice) => void;
  onViewDetails: (device: LitmusEdgeDevice) => void;
}

// Only the four columns the operator scans a device list for are shown
// inline (IP/Vendor/Product Name/Product Type); everything else Litmus Edge
// reported (MAC, interface, last seen, protocols, per-protocol details) lives
// in the "View details" dialog behind the row's kebab menu instead, so a
// wide device inventory stays scannable as a table.
function LitmusEdgeDeviceRow({ device, onAddSource, onViewDetails }: LitmusEdgeDeviceRowProps) {
  const canAddSource = !!device.connectorId;
  // "Coming soon" only makes sense when Litmus Edge identified something
  // about the device; an IP-only row (no vendor/product at all) gets no
  // action label.
  const hasDetails = [device.vendor, device.product_name, device.product_type].some(
    (v) => !!v?.trim(),
  );
  return (
    <tr className="hover:bg-paper-subtle">
      <td className="whitespace-nowrap px-4 py-3 font-mono text-[12.5px] text-ink">
        {device.ip_address || <span className="text-rule">—</span>}
      </td>
      <td className="px-4 py-3 text-ink">
        {device.vendor || <span className="text-rule">—</span>}
      </td>
      <td className="px-4 py-3 text-ink">
        {device.product_name || <span className="text-rule">—</span>}
      </td>
      <td className="px-4 py-3 text-ash">
        {device.product_type || <span className="text-rule">—</span>}
      </td>
      <td className="px-4 py-3 text-right">
        <div className="flex items-center justify-end gap-2">
          {canAddSource ? (
            <button
              type="button"
              onClick={() => onAddSource(device)}
              className="whitespace-nowrap rounded-md bg-litmus-dark-green-800 px-2.5 py-1.5 text-[12px] font-medium text-white transition-colors hover:bg-litmus-dark-green-700"
            >
              Add Source
            </button>
          ) : hasDetails ? (
            <span
              className="whitespace-nowrap text-[12px] italic text-ash"
              title="No connector is available yet for this device"
            >
              Coming soon
            </span>
          ) : null}
          <LitmusEdgeDeviceMenu device={device} onViewDetails={onViewDetails} />
        </div>
      </td>
    </tr>
  );
}

interface LitmusEdgeDeviceMenuProps {
  device: LitmusEdgeDevice;
  onViewDetails: (device: LitmusEdgeDevice) => void;
}

// Per-row "⋮" menu, currently just "View details" - kept as its own menu
// (rather than folding into the Add Source button) so more row-level
// actions can be added later without crowding the row.
function LitmusEdgeDeviceMenu({ device, onViewDetails }: LitmusEdgeDeviceMenuProps) {
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    function handleClickOutside(e: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [open]);

  return (
    <div ref={containerRef} className="relative inline-block text-left">
      <button
        type="button"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label="Device actions"
        onClick={() => setOpen((v) => !v)}
        className="flex h-7 w-7 items-center justify-center rounded-md text-ash transition-colors hover:bg-rule/40 hover:text-ink"
      >
        <svg width="14" height="14" viewBox="0 0 16 16" fill="currentColor" aria-hidden>
          <circle cx="8" cy="2.5" r="1.5" />
          <circle cx="8" cy="8" r="1.5" />
          <circle cx="8" cy="13.5" r="1.5" />
        </svg>
      </button>

      {open && (
        <div
          role="menu"
          className="absolute right-0 z-10 mt-1 w-40 overflow-hidden rounded-md border border-rule bg-surface shadow-lg"
        >
          <button
            type="button"
            role="menuitem"
            onClick={() => {
              setOpen(false);
              onViewDetails(device);
            }}
            className="block w-full px-3 py-2 text-left text-[12.5px] text-ink transition-colors hover:bg-paper-subtle"
          >
            View details
          </button>
        </div>
      )}
    </div>
  );
}

// firstProtocolValue returns the first non-empty value of key across the
// per-protocol blocks in protocols_data (e.g. cip.vendor_id), since Litmus
// Edge nests fields under the protocol that reported them rather than at the
// top level.
function firstProtocolValue(data: Record<string, unknown>, key: string): unknown {
  for (const block of Object.values(data ?? {})) {
    if (block && typeof block === "object" && !Array.isArray(block)) {
      const v = (block as Record<string, unknown>)[key];
      if (v !== undefined && v !== null && v !== "") return v;
    }
  }
  return undefined;
}

// Accepts a JSON number or a numeric string and returns it as a
// non-negative integer, or undefined when it isn't one.
function toNonNegativeInt(v: unknown): number | undefined {
  const n = typeof v === "number" ? v : typeof v === "string" && /^\d+$/.test(v.trim()) ? Number(v) : NaN;
  return Number.isInteger(n) && n >= 0 ? n : undefined;
}

// Parses a hex string with or without a 0x prefix (Litmus Edge reports CIP
// vendor_id as e.g. "2f" and serial as e.g. "0137e052" - always hex, even
// when every digit happens to be 0-9, like vendor_id "1").
function parseHex(v: unknown): number | undefined {
  if (typeof v !== "string") return undefined;
  const m = /^(?:0[xX])?([0-9A-Fa-f]+)$/.exec(v.trim());
  return m ? parseInt(m[1], 16) : undefined;
}

// staticMetadataFromDevice carries the identity a scan already captured into
// the Add Source dialog's Static Metadata section, so it doesn't have to be
// re-entered by hand. Vendor/product name/type come from the top-level scan
// fields; Vendor ID, Product Code, Serial Number and firmware revision come
// from protocols_data when CIP reported them. Each
// quick-add value is only set when it passes that field's own format check,
// so a prefill can never leave the form in an invalid state, and zero/empty
// values are skipped per the devprops inclusion rules.
function staticMetadataFromDevice(device: LitmusEdgeDevice): UserStructuredProps {
  const quickAdd: Record<string, string> = {};
  const data = device.protocols_data ?? {};

  // VENDOR_ID and SERIAL_NUMBER are EtherNet/IP identity values (see
  // devprops.VendorID), so they're read from the cip block only - a
  // Profinet vendor_id (e.g. "0x002a") comes from a different vendor
  // registry and would mislabel the device if stamped as VENDOR_ID.
  const cip = data.cip && typeof data.cip === "object" ? (data.cip as Record<string, unknown>) : {};
  const vendorId = parseHex(cip.vendor_id);
  const serialInt = parseHex(cip.serial);
  const serial = serialInt ? `0x${serialInt.toString(16).toUpperCase().padStart(8, "0")}` : undefined;
  const productCode = toNonNegativeInt(firstProtocolValue(data, "product_code"));

  const candidates: Record<string, string | undefined> = {
    VENDOR_ID: vendorId !== undefined ? String(vendorId) : undefined,
    VENDOR_NAME: device.vendor?.trim(),
    PRODUCT_NAME: device.product_name?.trim(),
    PRODUCT_CODE: productCode ? String(productCode) : undefined,
    DEVICE_TYPE: device.product_type?.trim(),
    SERIAL_NUMBER: serial,
  };
  for (const spec of QUICK_ADD_SPECS) {
    const v = candidates[spec.propertyId];
    if (!v) continue;
    if (spec.pattern && !spec.pattern.test(v)) continue;
    quickAdd[spec.propertyId] = v;
  }

  const result: UserStructuredProps = {};
  if (Object.keys(quickAdd).length > 0) result.quickAdd = quickAdd;

  const revision = firstProtocolValue(data, "revision");
  if (revision && typeof revision === "object") {
    const major = toNonNegativeInt((revision as Record<string, unknown>).major);
    const minor = toNonNegativeInt((revision as Record<string, unknown>).minor);
    if ((major ?? 0) > 0 || (minor ?? 0) > 0) {
      result.firmwareVersion = `${major ?? 0}.${minor ?? 0}`;
    }
  }
  return result;
}

// Human-readable labels for the raw per-protocol fields Litmus Edge reports
// under protocols_data (e.g. protocols_data.cip.vendor_id). Kept as a lookup
// rather than one formatter per protocol, since the same field names recur
// across protocols (cip, profinet) and new protocols need no code change to
// show reasonably - see keyLabel's title-case fallback below.
const PROTOCOL_FIELD_LABELS: Record<string, string> = {
  vendor_id: "Vendor ID",
  product_id: "Product ID",
  product_code: "Product Code",
  device_vendor_value: "Device Model",
  gateway: "Gateway",
  subnet_mask: "Subnet Mask",
  port: "Port",
  state: "State",
  status: "Status",
  serial: "Serial Number",
  revision: "Revision",
  major: "Major",
  minor: "Minor",
  encap_protocol_version: "Encapsulation Protocol Version",
};

// keyLabel renders an arbitrary protocols_data field name as a readable
// label, falling back to title-cased snake_case for anything not in
// PROTOCOL_FIELD_LABELS so a protocol/field this dialog has no special
// knowledge of still shows something reasonable instead of being dropped.
function keyLabel(key: string): string {
  if (PROTOCOL_FIELD_LABELS[key]) return PROTOCOL_FIELD_LABELS[key];
  return key
    .split("_")
    .map((w) => (w ? w[0].toUpperCase() + w.slice(1) : w))
    .join(" ");
}

// Renders one scalar protocols_data value for display. Nested objects (e.g.
// cip.revision = {major, minor}) are flattened by the caller instead of
// reaching here - see protocolDetailRows.
function formatProtocolValue(value: unknown): string {
  if (value === null || value === undefined || value === "") return "—";
  if (typeof value === "object") return JSON.stringify(value);
  return String(value);
}

interface DetailRow {
  key: string;
  label: string;
  value: string;
}

// Flattens one protocol's field object into label/value rows, inlining one
// level of nested objects (e.g. revision.major/revision.minor) as
// "Revision Major"/"Revision Minor" so firmware-style {major, minor} blocks
// read naturally instead of showing as raw JSON.
function protocolDetailRows(protocol: string, fields: Record<string, unknown>): DetailRow[] {
  const rows: DetailRow[] = [];
  for (const [key, value] of Object.entries(fields)) {
    if (value && typeof value === "object" && !Array.isArray(value)) {
      for (const [nestedKey, nestedValue] of Object.entries(value as Record<string, unknown>)) {
        rows.push({
          key: `${protocol}.${key}.${nestedKey}`,
          label: `${keyLabel(key)} ${keyLabel(nestedKey)}`,
          value: formatProtocolValue(nestedValue),
        });
      }
      continue;
    }
    rows.push({ key: `${protocol}.${key}`, label: keyLabel(key), value: formatProtocolValue(value) });
  }
  return rows;
}

interface LitmusEdgeDeviceDetailDialogProps {
  device: LitmusEdgeDevice | null;
  onOpenChange: (open: boolean) => void;
}

// "Device Information" modal shown for a discovered device's "View details"
// action - a header identifying the device (logo, name, connector-match
// status), a General section mirroring the app's other device-detail
// dialogs (IP/Last Seen/MAC/Vendor/Interface/Protocols/Ports/Hostname), and
// a Protocol Details section per protocol Litmus Edge reported on, since
// those fields don't fit the fixed table columns.
function LitmusEdgeDeviceDetailDialog({ device, onOpenChange }: LitmusEdgeDeviceDetailDialogProps) {
  const open = device !== null;
  const protocolSections = (device
    ? Object.entries(device.protocols_data ?? {}).map(([protocol, fields]) => ({
        protocol,
        rows: protocolDetailRows(protocol, (fields ?? {}) as Record<string, unknown>),
      }))
    : []
  ).filter((s) => s.rows.length > 0);

  const title = device?.product_name || device?.vendor || "Device Information";
  const subtitle = [device?.vendor, device?.product_type].filter(Boolean).join(" · ");

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-40 bg-ink/40" />
        <Dialog.Content className="fixed left-1/2 top-1/2 z-50 flex max-h-[85vh] w-full max-w-lg -translate-x-1/2 -translate-y-1/2 flex-col overflow-hidden rounded-lg bg-surface shadow-xl">
          {/* Positioned on Dialog.Content itself (not inside the scrolling
              body below), so it stays pinned to the top-right corner of the
              dialog regardless of how far the content is scrolled. */}
          <Dialog.Close
            aria-label="Close"
            className="absolute right-3 top-3 z-10 flex h-8 w-8 items-center justify-center rounded-md text-ash transition-colors hover:bg-rule/40 hover:text-ink"
          >
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden>
              <path
                d="M3 3l10 10M13 3L3 13"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
              />
            </svg>
          </Dialog.Close>

          {device && (
            <div className="flex items-start gap-3 border-b border-rule bg-paper-subtle px-6 py-5 pr-12">
              <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md border border-rule bg-surface">
                <PlatformLogo platformId={device.connectorId || "unknown"} className="h-6 w-6" />
              </div>
              <div className="min-w-0">
                <Dialog.Title className="truncate font-display text-[16.5px] font-semibold text-ink">
                  {title}
                </Dialog.Title>
                {subtitle && <p className="mt-0.5 truncate text-[12.5px] text-ash">{subtitle}</p>}
                <div className="mt-2">
                  {device.connectorId ? (
                    <span className="inline-flex items-center rounded-full bg-emerald-100 px-2 py-0.5 text-[11px] font-medium text-emerald-700">
                      Matches connector: {device.connectorName || device.connectorId}
                    </span>
                  ) : (
                    <span className="inline-flex items-center rounded-full bg-rule/40 px-2 py-0.5 text-[11px] font-medium text-ash">
                      No matching connector yet
                    </span>
                  )}
                </div>
              </div>
            </div>
          )}
          {!device && (
            <Dialog.Title className="sr-only">Device Information</Dialog.Title>
          )}

          <div className="overflow-y-auto px-6 py-5">
            {device && (
              <>
                <h3 className="mb-2 text-[12px] font-semibold uppercase tracking-wide text-ash">
                  General
                </h3>
                <div className="divide-y divide-rule rounded-md border border-rule">
                  <InfoRow label="IP" value={device.ip_address} mono />
                  <InfoRow label="Last Seen" value={formatEpochSeconds(device.last_seen)} />
                  <InfoRow label="MAC" value={device.mac_address} mono />
                  <InfoRow label="Vendor" value={device.vendor} />
                  <InfoRow label="Interface" value={device.interface} />
                  <InfoRow
                    label="Protocols"
                    value={device.protocols?.length ? device.protocols.join(", ") : ""}
                  />
                  <InfoRow label="Ports" value="" />
                  <InfoRow label="Hostname" value="" />
                </div>

                {protocolSections.length > 0 && (
                  <>
                    <h3 className="mb-2 mt-6 text-[12px] font-semibold uppercase tracking-wide text-ash">
                      Protocol Details
                    </h3>
                    <div className="space-y-3">
                      {protocolSections.map((s) => (
                        <div key={s.protocol} className="overflow-hidden rounded-md border border-rule">
                          <div className="border-b border-rule bg-paper-subtle px-3 py-1.5 text-[11px] font-semibold uppercase tracking-wide text-ash">
                            {s.protocol}
                          </div>
                          <div className="divide-y divide-rule">
                            {s.rows.map((row) => (
                              <InfoRow key={row.key} label={row.label} value={row.value} mono />
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                  </>
                )}
              </>
            )}
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

// One label/value row within a bordered info card - used for both the
// General section and each Protocol Details card, so device metadata reads
// as a compact, aligned list instead of a sparsely-spaced grid (most fields
// here are short strings, not paragraph content).
function InfoRow({ label, value, mono }: { label: string; value: string; mono?: boolean }) {
  return (
    <div className="flex items-center gap-4 px-3 py-2 text-[13px]">
      <span className="w-28 shrink-0 text-ash">{label}</span>
      <span className={cn("truncate text-ink", mono && "font-mono text-[12.5px]")}>
        {value || <span className="text-rule">—</span>}
      </span>
    </div>
  );
}
